import { DragIndicator } from "@mui/icons-material";
import { Box, Paper, Typography } from "@mui/material";
import React, { useCallback, useRef } from "react";
import { DndProvider, useDrag, useDrop, XYCoord } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import theme from "Theme/AppTheme";

export type AcxSortableItem = { id: string; label: string };

type Props = {
    items: AcxSortableItem[];
    /** Called with the full set of ids in their new order after a drag. */
    onReorder: (orderedIds: string[]) => void;
    idPrefix?: string;
};

const ITEM_TYPE = "acx-sortable-item";

type DragItem = { type: string; index: number; id: string };

type RowProps = {
    item: AcxSortableItem;
    index: number;
    move: (from: number, to: number) => void;
    idPrefix?: string;
};

const SortableRow: React.FC<RowProps> = ({ item, index, move, idPrefix }) => {
    const ref = useRef<HTMLDivElement>(null);

    const [, drop] = useDrop({
        accept: ITEM_TYPE,
        hover(dragged: DragItem, monitor) {
            if (!ref.current) return;
            const dragIndex = dragged.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;

            // Only swap once the cursor passes the row's vertical midpoint, so a hovered row
            // doesn't flip back and forth as the pointer crosses its edge.
            const rect = ref.current.getBoundingClientRect();
            const middleY = (rect.bottom - rect.top) / 2;
            const clientY = (monitor.getClientOffset() as XYCoord).y - rect.top;
            if (dragIndex < hoverIndex && clientY < middleY) return;
            if (dragIndex > hoverIndex && clientY > middleY) return;

            move(dragIndex, hoverIndex);
            dragged.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        item: { type: ITEM_TYPE, index, id: item.id } as DragItem,
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    // Whole row is the drag preview + drop target; the handle initiates the drag.
    drop(preview(ref));

    return (
        <Paper
            ref={ref}
            id={idPrefix ? `${idPrefix}-${item.id}` : undefined}
            variant="outlined"
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1,
                py: 1,
                minWidth: 0,
                opacity: isDragging ? 0.4 : 1,
            }}
        >
            <Box
                ref={drag}
                sx={{
                    display: "flex",
                    alignItems: "center",
                    cursor: "grab",
                    color: theme.palette.gray[400],
                }}
            >
                <DragIndicator fontSize="small" />
            </Box>
            <Typography
                variant="body2"
                sx={{
                    color: theme.palette.gray[700],
                    minWidth: 0,
                    overflowWrap: "anywhere",
                }}
            >
                {item.label}
            </Typography>
        </Paper>
    );
};

/**
 * Generic vertical drag-to-reorder list. Stateless: it renders `items` in the given order and reports
 * the new order via `onReorder` — the parent owns the ordered ids. Uses react-dnd (the repo's dnd lib).
 */
const AcxSortableList: React.FC<Props> = ({ items, onReorder, idPrefix }) => {
    const move = useCallback(
        (from: number, to: number) => {
            const next = [...items];
            const [moved] = next.splice(from, 1);
            next.splice(to, 0, moved);
            onReorder(next.map((i) => i.id));
        },
        [items, onReorder],
    );

    return (
        // @ts-expect-error: error coming from outdated react-dnd package prop types
        <DndProvider backend={HTML5Backend}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                {items.map((item, index) => (
                    <SortableRow
                        key={item.id}
                        item={item}
                        index={index}
                        move={move}
                        idPrefix={idPrefix}
                    />
                ))}
            </Box>
        </DndProvider>
    );
};

export default AcxSortableList;
